import { op, Operation } from "libs/transaction";
import { generateRecordId, getPointer, RecordValue, ThreadType } from "libs/schema";
import { isDefined } from "libs/predicates";

export type AddMinimalDoneNotificationForThreadIfOneDoesNotExistProps = {
  userId: string;
  threadId: string;
  threadType: ThreadType;
  messageId: string;
  messageSentAt: string;
  ownerOrganizationId: string;
};

/**
 * If we need a user to have a notification for a thread for technical purposes,
 * use this method. If conditionally creates a "done" notification for a user if
 * they don't already have one for the thread and gives the notification the lowest
 * priority possible. The notification is created with "is_deleted" set to false and
 * no tag_ids.
 */
export function addMinimalDoneNotificationForThreadIfOneDoesNotExist(
  props: AddMinimalDoneNotificationForThreadIfOneDoesNotExistProps,
): Operation[] {
  const pointer = getPointer("notification", {
    user_id: props.userId,
    thread_id: props.threadId,
  });

  return [
    op.upsert(pointer, {
      onCreate: [
        op.set(pointer.table, {
          id: pointer.id,
          thread_id: props.threadId,
          user_id: props.userId,
          thread_type: props.threadType,
          message_id: props.messageId,
          tag_ids: [],
          sent_at: props.messageSentAt,
          is_done: true,
          done_at: op.fieldValue.SERVER_TIMESTAMP(),
          priority: 500,
          request_priority: null,
          respond_by: null,
          respond_by_tz: null,
          done_last_modified_by: "delivery",
          oldest_message_not_marked_done_message_id: null,
          oldest_message_not_marked_done_sent_at: null,
          has_reminder: false,
          remind_at: null,
          is_starred: false,
          starred_at: null,
          owner_organization_id: props.ownerOrganizationId,
          is_delivered: false,
          delivered_at: null,
          data: null,
        }),
      ],
    }),
  ];
}

/* -----------------------------------------------------------------------------------------------*/

export type TriageNotificationProps = {
  notification: Pick<
    RecordValue<"notification">,
    | "id"
    | "user_id"
    | "thread_id"
    | "oldest_message_not_marked_done_message_id"
    | "oldest_message_not_marked_done_sent_at"
    | "sent_at"
    | "message_id"
    | "owner_organization_id"
  >;
  /**
   * If the user has lost permission to access this thread, provide `null`. Else
   * provide the appropriate values.
   */
  lastMessage: {
    id: string;
    timelineOrder: string;
    sentAt: string;
  } | null;
  currentTimestamp: string;
  isDone?: boolean;
  remindAt?: string | null;
  isStarred?: boolean;
  areRequestsResolved?: true;
};

export function triageNotification(props: TriageNotificationProps): Operation[] {
  if (
    !isDefined(props.isDone) &&
    !isDefined(props.remindAt) &&
    !isDefined(props.isStarred) &&
    !isDefined(props.areRequestsResolved)
  ) {
    throw new Error("[triageNotification] missing prop to triage.");
  }

  const { notification, lastMessage } = props;

  const operations: Operation[] = [];

  const notificationUpdate: Partial<
    Pick<
      RecordValue<"notification">,
      | "is_done"
      | "done_at"
      | "done_last_modified_by"
      | "oldest_message_not_marked_done_message_id"
      | "oldest_message_not_marked_done_sent_at"
      | "has_reminder"
      | "remind_at"
      | "is_starred"
      | "starred_at"
      | "request_priority"
      | "respond_by"
      | "respond_by_tz"
    >
  > = {};

  if (isDefined(props.isDone)) {
    notificationUpdate.done_last_modified_by = "user";

    if (props.isDone) {
      notificationUpdate.is_done = true;
      notificationUpdate.done_at = props.currentTimestamp;
      notificationUpdate.oldest_message_not_marked_done_message_id = null;
      notificationUpdate.oldest_message_not_marked_done_sent_at = null;
    } else {
      notificationUpdate.is_done = false;
      notificationUpdate.done_at = null;

      const currentOldestMessageNotMarkedDoneMessageId =
        notification.oldest_message_not_marked_done_message_id || notification.message_id;

      const currentOldestMessageNotMarkedDoneSentAt =
        notification.oldest_message_not_marked_done_sent_at || notification.sent_at;

      if (lastMessage && lastMessage.sentAt < currentOldestMessageNotMarkedDoneSentAt) {
        notificationUpdate.oldest_message_not_marked_done_message_id = lastMessage.id;
        notificationUpdate.oldest_message_not_marked_done_sent_at = lastMessage.sentAt;
      } else {
        notificationUpdate.oldest_message_not_marked_done_message_id = currentOldestMessageNotMarkedDoneMessageId;
        notificationUpdate.oldest_message_not_marked_done_sent_at = currentOldestMessageNotMarkedDoneSentAt;
      }
    }
  }

  if (isDefined(props.remindAt)) {
    notificationUpdate.has_reminder = !!props.remindAt;
    notificationUpdate.remind_at = props.remindAt || null;
  }

  if (isDefined(props.isStarred)) {
    notificationUpdate.is_starred = props.isStarred;
    notificationUpdate.starred_at = props.isStarred ? props.currentTimestamp : null;
  }

  if (isDefined(props.areRequestsResolved)) {
    if (props.areRequestsResolved) {
      notificationUpdate.request_priority = null;
      notificationUpdate.respond_by = null;
      notificationUpdate.respond_by_tz = null;
    }
  }

  if (
    // If the user has lost permission to access this thread, we don't set the read receipts.
    lastMessage &&
    notificationUpdate.is_done &&
    // setting a reminder shouldn't mark a thread as "read".
    !notificationUpdate.has_reminder
  ) {
    operations.push(
      op.set("thread_read_receipt", {
        id: generateRecordId("thread_read_receipt", {
          thread_id: notification.thread_id,
          user_id: notification.user_id,
        }),
        owner_organization_id: notification.owner_organization_id,
        thread_id: notification.thread_id,
        user_id: notification.user_id,
        read_to_timeline_id: generateRecordId("thread_timeline", {
          entry_id: lastMessage.id,
          thread_id: notification.thread_id,
        }),
        read_to_timeline_order: lastMessage.timelineOrder,
      }),

      op.set("thread_seen_receipt", {
        id: generateRecordId("thread_seen_receipt", {
          thread_id: notification.thread_id,
          user_id: notification.user_id,
        }),
        owner_organization_id: notification.owner_organization_id,
        thread_id: notification.thread_id,
        user_id: notification.user_id,
        seen_to_timeline_id: generateRecordId("thread_timeline", {
          entry_id: lastMessage.id,
          thread_id: notification.thread_id,
        }),
        seen_to_timeline_order: lastMessage.timelineOrder,
      }),
    );
  }

  operations.push(op.update({ table: "notification", id: notification.id }, notificationUpdate));

  return operations;
}

/* -----------------------------------------------------------------------------------------------*/
